import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../components/Layout/LanguageContext';
import { FaDownload, FaArrowLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const WorkSamplesSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-color);
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--background-light);
    transform: translateX(-4px);
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-secondary);
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SampleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 3rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const SampleCard = styled(motion.div)`
  background: var(--background-light);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 12px 40px rgba(0, 51, 102, 0.1);
  border: 1px solid var(--background-dark);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 51, 102, 0.15);
    border-color: var(--accent-color);
  }
`;

const SampleHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SampleIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: white;
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
`;

const SampleInfo = styled.div`
  flex: 1;
`;

const SampleTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.3rem;
`;

const SampleDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin: 0;
`;

const SampleImage = styled.div`
  position: relative;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
  
  ${SampleCard}:hover & {
    transform: scale(1.02);
  }
`;

const DownloadButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--primary-color);
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(0, 51, 102, 0.3);
  }
`;

const workSamplesContent = {
  de: {
    title: 'Arbeitsproben',
    subtitle: 'Konkrete Beispiele meiner Arbeit und Lösungen aus der Praxis',
    backButton: 'Zurück zur Startseite',
    powerBiSample: {
      title: 'Sales Report mit Kundenanalyse',
      description: 'Ein professioneller Sales Report mit visueller Kunden- und Produktanalyse zur Darstellung von Umsätzen, Margen und Bestellverhalten.',
      downloadText: 'Vollbild anzeigen'
    },
    powerBiSample2: {
      title: 'Finance Report mit Wasserfall-Diagramm',
      description: 'Ein professioneller Finance Report mit detailliertem Wasserfall-Diagramm zur Visualisierung von Finanzdaten und Trends.',
      downloadText: 'Vollbild anzeigen'
    },
    powerBiSample3: {
      title: 'Power BI Report mit ABCD-Analyse',
      description: 'Ein umfassender Power BI Report mit ABCD-Analyse über Gewinn und Umsatz, zur strategischen Geschäftssteuerung.',
      downloadText: 'Vollbild anzeigen'
    },
    airflowSample: {
      title: 'Automatisierter Meeting-Intelligenz-Flow',
      description: 'Meeting-Aufnahmen werden automatisch transkribiert, in Azure AI Search indiziert und als durchsuchbarer Chatbot in Copilot Studio bereitgestellt.',
      downloadText: 'Vollbild anzeigen'
    },
    automationSample: {
      title: 'Automatisierte Dokumentverarbeitung',
      description: 'Automatisierte Dokumentverarbeitung mit Abgleich in OneDrive, Gruppierung nach Policennummer, Versand an Kunden/Agenten und Übersicht an den Admin. Integriertes Fehlerhandling sorgt für zuverlässigen Ablauf.',
      downloadText: 'Vollbild anzeigen'
    },
    assessmentSample: {
      title: 'Power-Apps Fragebogen- & Prozesssteuerung',
      description: 'Komplexe Power-Apps-Lösung mit dynamischen Fragebogenlogiken, automatisierten mehrstufigen Freigaben und Eskalationen in Power Automate. Integrierte Datenvalidierung, Dataverse-Konsolidierung sowie nahtlose Übergabe der Ergebnisse an SAP LeanIX.',
      downloadText: 'Vollbild anzeigen'
    }
  },
  en: {
    title: 'Work Samples',
    subtitle: 'Concrete examples of my work and solutions from practice',
    backButton: 'Back to Homepage',
    powerBiSample: {
      title: 'Sales Report with Customer Analysis',
      description: 'A professional sales report with visual customer and product analysis for displaying sales, margins and ordering behavior.',
      downloadText: 'View Full Size'
    },
    powerBiSample2: {
      title: 'Finance Report with Waterfall Chart',
      description: 'A professional finance report with detailed waterfall chart for visualizing financial data and trends.',
      downloadText: 'View Full Size'
    },
    powerBiSample3: {
      title: 'Power BI Report with ABCD Analysis',
      description: 'A comprehensive Power BI report with ABCD analysis over profit and revenue, for strategic business management.',
      downloadText: 'View Full Size'
    },
    airflowSample: {
      title: 'Automated Meeting Intelligence Flow',
      description: 'Meeting recordings are automatically transcribed, indexed in Azure AI Search and provided as a searchable chatbot in Copilot Studio.',
      downloadText: 'View Full Size'
    },
    automationSample: {
      title: 'Automated Document Processing',
      description: 'Automated document processing with OneDrive matching, grouping by policy number, sending to customers/agents and overview to admin. Integrated error handling ensures reliable workflow.',
      downloadText: 'View Full Size'
    },
    assessmentSample: {
      title: 'Power Apps Questionnaire & Process Orchestration',
      description: 'Complex Power Apps solution with dynamic questionnaire logic, automated multi-stage approvals and escalations in Power Automate. Includes integrated data validation, Dataverse consolidation and seamless hand-off of the results to SAP LeanIX.',
      downloadText: 'View Full Size'
    }
  }
};

const WorkSamples: React.FC = () => {
  const { language } = useLanguage();
  const t = workSamplesContent[language];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <WorkSamplesSection>
      <Container>
        <BackButton to="/">
          <FaArrowLeft />
          {t.backButton}
        </BackButton>

        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <SampleGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <SampleCard variants={cardVariants}>
            <SampleHeader>
              <SampleIcon>
                <img src="/images/powerbilogo" alt="Power BI" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </SampleIcon>
              <SampleInfo>
                <SampleTitle>{t.powerBiSample.title}</SampleTitle>
                <SampleDescription>{t.powerBiSample.description}</SampleDescription>
              </SampleInfo>
            </SampleHeader>
            
            <SampleImage>
              <Image 
                src="/images/BeispielReport.jpg" 
                alt="Power BI Report Beispiel"
              />
            </SampleImage>
            
            <DownloadButton 
              href="/images/BeispielReport.jpg" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload />
              {t.powerBiSample.downloadText}
            </DownloadButton>
          </SampleCard>

          <SampleCard variants={cardVariants}>
            <SampleHeader>
              <SampleIcon>
                <img src="/images/powerbilogo" alt="Power BI" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </SampleIcon>
              <SampleInfo>
                <SampleTitle>{t.powerBiSample2.title}</SampleTitle>
                <SampleDescription>{t.powerBiSample2.description}</SampleDescription>
              </SampleInfo>
            </SampleHeader>
            
            <SampleImage>
              <Image 
                src="/images/BeispielReport2.jpg" 
                alt="Power BI Dashboard Beispiel"
              />
            </SampleImage>
            
            <DownloadButton 
              href="/images/BeispielReport2.jpg" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload />
              {t.powerBiSample2.downloadText}
            </DownloadButton>
          </SampleCard>

          <SampleCard variants={cardVariants}>
            <SampleHeader>
              <SampleIcon>
                <img src="/images/powerbilogo" alt="Power BI" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </SampleIcon>
              <SampleInfo>
                <SampleTitle>{t.powerBiSample3.title}</SampleTitle>
                <SampleDescription>{t.powerBiSample3.description}</SampleDescription>
              </SampleInfo>
            </SampleHeader>
            
            <SampleImage>
              <Image 
                src="/images/BeispielReport3.jpg" 
                alt="Power BI Report mit ABCD-Analyse"
              />
            </SampleImage>
            
            <DownloadButton 
              href="/images/BeispielReport3.jpg" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload />
              {t.powerBiSample3.downloadText}
            </DownloadButton>
          </SampleCard>

          <SampleCard variants={cardVariants}>
            <SampleHeader>
              <SampleIcon>
                <img src="/images/copilot.png" alt="Airflow" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </SampleIcon>
              <SampleInfo>
                <SampleTitle>{t.airflowSample.title}</SampleTitle>
                <SampleDescription>{t.airflowSample.description}</SampleDescription>
              </SampleInfo>
            </SampleHeader>
            
            <SampleImage>
              <Image 
                src="/images/Aiflow.jpg" 
                alt="Automatisierter Meeting-Intelligenz-Flow"
              />
            </SampleImage>
            
            <DownloadButton 
              href="/images/Aiflow.jpg" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload />
              {t.airflowSample.downloadText}
            </DownloadButton>
          </SampleCard>

          <SampleCard variants={cardVariants}>
            <SampleHeader>
              <SampleIcon>
                <img src="/images/automatelogo.png" alt="Automation" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </SampleIcon>
              <SampleInfo>
                <SampleTitle>{t.automationSample.title}</SampleTitle>
                <SampleDescription>{t.automationSample.description}</SampleDescription>
              </SampleInfo>
            </SampleHeader>
            
            <SampleImage>
              <Image 
                src="/images/flow.png" 
                alt="Automatisierte Dokumentverarbeitung"
              />
            </SampleImage>
            
            <DownloadButton 
              href="/images/flow.png" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload />
              {t.automationSample.downloadText}
            </DownloadButton>
          </SampleCard>

          <SampleCard variants={cardVariants}>
            <SampleHeader>
              <SampleIcon>
                <img src="/images/appslogo.jpg" alt="Assessment App" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              </SampleIcon>
              <SampleInfo>
                <SampleTitle>{t.assessmentSample.title}</SampleTitle>
                <SampleDescription>{t.assessmentSample.description}</SampleDescription>
              </SampleInfo>
            </SampleHeader>
            
            <SampleImage>
              <Image 
                src="/images/app1.png" 
                alt="Assessment- und Risiko-Management"
              />
            </SampleImage>
            
            <DownloadButton 
              href="/images/app1.png" 
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload />
              {t.assessmentSample.downloadText}
            </DownloadButton>
          </SampleCard>

        </SampleGrid>
      </Container>
    </WorkSamplesSection>
  );
};

export default WorkSamples;

